import { consume } from '@lit/context';
import { gsap } from 'gsap';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, query } from 'lit/decorators.js';

import { gameApiContext } from '../../context';
import type { GameAPIType } from '../../GameAPI';
import { scrollbarThin } from '../../theme/scrollbar.css';
import { tokens } from '../../theme/tokens.css';
import { type KeybindItem, RADIO_CHANNELS, uiState as uiStateProxy } from '../../UIState';
import { bindValtio } from '../../ValtioController';

/**
 * Bind names shown in the visual "hero" section at the top of the panel
 * (movement arrows, fire, special, quick-upgrade). Excluded from the grouped
 * shortcuts list below so they aren't documented twice.
 */
const HERO_BINDS: ReadonlySet<string> = new Set([
  'UP',
  'DOWN',
  'LEFT',
  'RIGHT',
  'FIRE',
  'SPECIAL',
  'UPGRADE1',
  'UPGRADE2',
  'UPGRADE3',
  'UPGRADE4',
]);

/** Movement binds in the hero cluster's visual order (up, left, down, right).
 *  Each renders its primary key — an arrow glyph if that key is an arrow key,
 *  otherwise the key label (e.g. a WASD remap). */
const MOVEMENT_BINDS: readonly string[] = ['UP', 'LEFT', 'DOWN', 'RIGHT'];

/** Arrow key-name → CSS arrow-direction class for the keycap glyph. */
const ARROW_KEY_GLYPH: Readonly<Record<string, string>> = {
  UP: 'up',
  DOWN: 'down',
  LEFT: 'left',
  RIGHT: 'right',
};

/** A row for something that isn't a remappable bind: a fixed contextual key
 *  (ENTER for chat, the radio's Z/X/C) or a mouse gesture. */
interface LiteralShortcut {
  keys: readonly string[];
  label: string;
  note?: string;
}

/** A category of shortcuts in the grouped reference list. */
interface ShortcutCategory {
  title: string;
  /** In display order: bind names, resolved reactively from the live keybind
   *  config, and literal rows. */
  rows: readonly (string | LiteralShortcut)[];
}

/**
 * Grouped shortcut reference. Labels and keys are resolved from the live
 * keybind config at render time, so they track user remaps. Any bind that
 * exists in the config but is listed neither here nor in HERO_BINDS is caught
 * by the "Other" group, so a newly-added bind can never silently go
 * undocumented.
 *
 * Order matters for the two-column (CSS multi-column) layout: categories are
 * kept whole (break-inside: avoid), so the column break can only land between
 * them. Row counts incl. titles are 5/5/8 (Combat/Camera/Game) and 9/9
 * (Interface/Chat) — 36 total — so the balancer breaks cleanly at the midpoint
 * (18/18): Combat+Camera+Game fill the left column, Interface+Chat the right.
 * Splitting the old oversized "Interface" group into Game + Interface is what
 * creates that mid-list break point.
 */
const SHORTCUT_CATEGORIES: readonly ShortcutCategory[] = [
  { title: 'Combat', rows: ['STRAFELEFT', 'STRAFERIGHT', 'DROP', 'LASER'] },
  { title: 'Camera', rows: ['ZOOMOUT', 'ZOOMIN', 'DEFAULTZOOM', 'ALTZOOM'] },
  {
    title: 'Game',
    rows: [
      'FULLSCREEN',
      'SOUND',
      'MOUSEMODE',
      'SPECTATE',
      'CRUISE',
      // Alt+click on the minimap (UI.ts); nothing else in the game mentions it
      { keys: ['ALT+CLICK'], label: 'Autopilot', note: 'on minimap' },
      'HIDEUI',
    ],
  },
  {
    title: 'Interface',
    rows: ['SHOWSCORE', 'LEADERBOARD', 'MAINMENU', 'KEYBINDS', 'SHOWGAMES', 'INVITE', 'HELP', 'EMOTES'],
  },
  {
    title: 'Chat',
    rows: [
      { keys: ['ENTER'], label: 'Public chat' },
      'SAY',
      'TEAM',
      'REPLY',
      // All, Team (CTF only) and Close Range: fixed keys, as in StarMash
      { keys: RADIO_CHANNELS.map((channel) => channel.key), label: 'Radio' },
      'MAXIMIZECHAT',
      'MINIMIZECHAT',
      'CLEARCHAT',
    ],
  },
];

/** Bind names the categories list, so the "Other" group can catch the rest. */
const CATEGORIZED_BINDS: ReadonlySet<string> = new Set(
  SHORTCUT_CATEGORIES.flatMap((cat) => cat.rows.filter((row): row is string => typeof row === 'string')),
);

/**
 * Per-bind qualifier appended to the label in the reference, for controls
 * that only apply in certain contexts (e.g. plane-specific abilities). Keyed
 * by bind name.
 */
const BIND_NOTES: Readonly<Record<string, string>> = {
  STRAFELEFT: 'Mohawk only',
  STRAFERIGHT: 'Mohawk only',
  DROP: 'flag / upgrade',
};

/** Chat commands — typed into chat, not keybinds, so they stay static. */
const CHAT_COMMANDS: ReadonlyArray<{ cmd: string; desc: string }> = [
  { cmd: '/s', desc: 'In-game say' },
  { cmd: '/t', desc: 'Team chat' },
  { cmd: '/w', desc: 'Whisper player' },
  { cmd: '/ignore', desc: 'Ignore player' },
  { cmd: '/unignore', desc: 'Unignore player' },
  { cmd: '/votemute', desc: 'Votemute player' },
  { cmd: '/spectate', desc: 'Spectate a player' },
  { cmd: '/respawn', desc: 'Respawn as plane' },
  { cmd: '/upgrade', desc: 'Apply upgrade' },
  { cmd: '/drop', desc: 'Drop flag / upgrade' },
  { cmd: '/flag', desc: 'Change flag' },
  { cmd: '/emotes', desc: 'Emotes list' },
  { cmd: '/help', desc: 'Show this help' },
];

/**
 * Help Panel - Shows keyboard controls and game commands
 *
 * Displays how-to-play information with keyboard shortcuts and chat commands.
 * Keyboard hints are driven by the live keybind config (`uiState.keybinds`),
 * so remaps are reflected and the grouped reference can't drift out of sync
 * with the actual binds.
 *
 * Features:
 * - Visual hero section: movement, fire, special, quick-upgrade
 * - Grouped, reactive shortcut reference with an "Other" catch-all
 * - Static chat-command reference
 * - Two panes on a wide screen, so the full help fits a 768px-high one
 * - Condensed mode for first-play (hides the reference sections)
 * - Mobile variant with "play on PC" message
 * - Closes on any key press or click, the panel included (matches original
 *   behavior)
 * - GSAP entrance/exit animations
 *
 * @element help-panel
 */
@customElement('help-panel')
export class HelpPanel extends LitElement {
  static override styles = [
    tokens,
    scrollbarThin,
    css`
      :host {
        display: block;
        /* Declared here rather than inherited from the body: the panel owns
           its type, and a tap to close it shouldn't start a text selection,
           callout or a grey flash across the whole screen. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
        /* Keycap visual is bespoke to this panel (the keybinds panel uses a
           different input-box style), so these stay component-local rather
           than in global tokens. Grouped here for reuse across the rules
           below; promote to tokens.css if a second component ever needs it. */
        --keycap-face: #f0f0f0;
        --keycap-text: #282828;
        --keycap-edge: #828282;
      }

      .backdrop {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 200;
        /* Drops the 300ms double-tap delay from tap-to-close. */
        touch-action: manipulation;
      }

      .panel {
        position: relative;
        box-sizing: border-box;
        width: 600px;
        max-width: calc(100vw - 32px);
        /* Of the backdrop, which is the screen you can see: unlike vh, that
           leaves room for a phone browser's toolbar. */
        max-height: 88%;
        overflow-y: auto;
        background-color: var(--color-bg-panel);
        box-shadow: var(--shadow-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        border-top: 2px solid var(--color-border-light);
        padding: 18px 18px 8px 18px;
        cursor: default;
      }

      /* Phones: a tap anywhere closes it, the panel included, since it nearly
         fills the screen. cursor: pointer is what makes iOS fire a click for
         a tap on a plain element; it inherits, so the children close on a tap
         too. A column, so that on a short landscape screen the controls
         graphic is what gives way. */
      .panel.mobile {
        width: 440px;
        display: flex;
        flex-direction: column;
        text-align: center;
        cursor: pointer;
      }

      /* ---- Layout: one column, or two panes on a wide screen ---- */

      /* One column, in legacy's order: the essentials, then the shortcut
         reference, then the chat commands. */
      .body {
        display: grid;
        grid-template-areas: 'essentials' 'reference' 'commands';
      }

      .essentials {
        grid-area: essentials;
      }

      .reference {
        grid-area: reference;
      }

      .chat-commands {
        grid-area: commands;
      }

      /* In one 600px column the full help is about 1040px tall, so a 768px
         screen hid a third of it. A wide screen puts the reference beside the
         rest, about 630px. 1064 is the 1000px panel plus a 32px gutter each
         side. The reference is the taller side, and the last row is 1fr so its
         extra height lands there rather than spreading out the keys beside it. */
      @media (min-width: 1064px) {
        .panel.full {
          width: 1000px;
        }

        .panel.full .body {
          grid-template-columns: 420px minmax(0, 1fr);
          grid-template-rows: auto auto 1fr;
          grid-template-areas:
            'essentials reference'
            'commands reference'
            '. reference';
          column-gap: 40px;
        }

        /* Its own height, so a screen too short for it scrolls the panel
           rather than squeezing the two columns into three. */
        .panel.full .reference {
          align-self: start;
        }

        /* Nothing follows the reference in its pane, so no gap after its last
           group: 20px, which is what gets the panel onto a 1280x720 screen. */
        .panel.full .shortcuts {
          padding-bottom: 0;
        }

        .panel.full .category:last-child {
          margin-bottom: 0;
        }
      }

      .header {
        width: 100%;
        text-align: center;
        font-weight: bold;
        font-size: var(--font-size-xxl);
        color: var(--color-text-white);
        padding-bottom: 10px;
      }

      .header .keybind-hint {
        font-weight: bold;
        margin-left: 10px;
        color: var(--color-text-muted);
        font-size: var(--font-size-lg);
      }

      .close {
        text-align: center;
        font-weight: bold;
        font-size: var(--font-size-16);
        color: var(--color-text-muted);
        padding-bottom: 24px;
      }

      /* ---- Hero section (movement / fire / special / upgrade) ---- */

      /* Centered two-column row: keycaps right-aligned, description left.
         Flex (not absolute fixed-widths) so the panel can shrink on narrow
         viewports without the two halves overlapping — both columns shrink
         and wrap instead. min-width: 0 lets them shrink below content width. */
      .keybind {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 38px;
        min-height: 48px;
      }

      .keys {
        flex: 0 1 240px;
        min-width: 0;
        text-align: right;
      }

      .keyor {
        font-size: var(--font-size-md);
        font-weight: bold;
        padding-left: 10px;
        padding-right: 4px;
        color: #b4b4b4; /* legacy's; no token */
      }

      .key {
        position: relative;
        display: inline-block;
        color: var(--keycap-text);
        background-color: var(--keycap-face);
        border-radius: var(--radius-md);
        padding: 7px 10px;
        margin-left: 6px;
        font-size: var(--font-size-16);
        font-weight: bold;
        box-shadow: 0 4px 0 0 var(--keycap-edge);
      }

      .key.small {
        box-sizing: border-box;
        min-width: 34px;
        padding: 7px 6px;
        text-align: center;
      }

      .key .arrow {
        position: absolute;
        top: 12px;
        left: 10px;
        width: 0;
        height: 0;
        border-style: solid;
      }

      .key .arrow.down {
        border-width: 12px 7px 0 7px;
        border-color: var(--keycap-text) transparent transparent transparent;
      }

      .key .arrow.up {
        border-width: 0 7px 12px 7px;
        border-color: transparent transparent var(--keycap-text) transparent;
      }

      .key .arrow.left {
        border-width: 7px 12px 7px 0;
        border-color: transparent var(--keycap-text) transparent transparent;
        top: 11px;
      }

      .key .arrow.right {
        border-width: 7px 0 7px 12px;
        border-color: transparent transparent transparent var(--keycap-text);
        top: 11px;
        left: 12px;
      }

      .keydesc {
        flex: 0 1 240px;
        min-width: 0;
        color: var(--color-text-link);
        font-size: var(--font-size-16);
        font-weight: bold;
      }

      .gamepadenabled {
        text-align: center;
        font-weight: bold;
        font-size: var(--font-size-16);
        color: #f0f0f0; /* legacy's; no text token */
        padding-bottom: 10px;
        padding-top: 10px;
        line-height: 48px;
      }

      /* ---- Grouped shortcut reference ---- */

      .section-title {
        text-align: center;
        font-weight: bold;
        font-size: var(--font-size-16);
        color: var(--color-text-muted);
        padding-bottom: 12px;
      }

      .shortcuts {
        columns: 2;
        column-gap: 28px;
        padding: 0 8px 8px 8px;
      }

      .category {
        break-inside: avoid;
        margin-bottom: 14px;
      }

      .category-title {
        font-size: var(--font-size-xs);
        font-weight: bold;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--color-text-secondary);
        margin-bottom: 8px;
      }

      .srow {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 7px;
      }

      .skeys {
        display: flex;
        gap: 4px;
        flex: 0 0 auto;
      }

      .skey {
        min-width: 14px;
        padding: 3px 7px;
        background-color: var(--keycap-face);
        color: var(--keycap-text);
        border-radius: var(--radius-sm);
        font-size: var(--font-size-xs);
        font-weight: bold;
        text-align: center;
        box-shadow: 0 2px 0 0 var(--keycap-edge);
      }

      .sor {
        align-self: center;
        font-size: var(--font-size-xxs);
        font-weight: bold;
        color: var(--color-text-secondary);
      }

      .slabel {
        font-size: var(--font-size-sm);
        font-weight: bold;
        color: var(--color-text-link);
      }

      .snote {
        margin-left: 6px;
        font-weight: normal;
        font-size: var(--font-size-xxs);
        color: var(--color-text-secondary);
      }

      /* ---- Chat commands (static) ---- */

      .cmdlist {
        text-align: center;
        font-weight: normal;
        padding-top: 4px;
      }

      /* A command and its description wrap as one. */
      .cmdlist .pair {
        display: inline-block;
        white-space: nowrap;
        margin-right: 6px;
        margin-bottom: 6px;
      }

      .cmdlist .cmd,
      .cmdlist .desc {
        display: inline-block;
        font-size: var(--font-size-13);
        color: var(--color-text-link);
        padding: 3px 7px;
      }

      .cmdlist .cmd {
        background-color: var(--color-bg-badge);
        border-top-left-radius: var(--radius-md);
        border-bottom-left-radius: var(--radius-md);
      }

      .cmdlist .desc {
        background-color: var(--color-bg-hover);
        border-top-right-radius: var(--radius-md);
        border-bottom-right-radius: var(--radius-md);
      }

      .mobile-graphic {
        display: block;
        /* Below its aspect-ratio height when the panel runs short of room;
           contain keeps the art whole. A flex item's automatic minimum would
           otherwise hold it at full height. */
        min-height: 0;
        width: 100%;
        aspect-ratio: 760 / 420;
        margin: 0 auto 4px;
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        background-image: url('/vanilla/ui/mobile-controls.svg');
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  @query('.panel') private panel?: HTMLElement;

  private isClosing = false;

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.handleKeyDown);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleKeyDown);
    if (this.panel) gsap.killTweensOf(this.panel);
  }

  override firstUpdated() {
    // Guard, don't reset: a keydown/backdrop close can land before first
    // render, and resetting the flag here would undo that close's guard and
    // play the entrance animation anyway.
    if (this.isClosing) return;
    this.animateIn();
  }

  // Only the panel fades: the backdrop is transparent, there just to catch
  // clicks.
  private animateIn() {
    if (!this.panel) return;
    gsap.killTweensOf(this.panel);
    gsap.fromTo(this.panel, { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 0.4, ease: 'power2.out' });
  }

  private animateOut(onComplete: () => void) {
    if (!this.panel) {
      onComplete();
      return;
    }
    gsap.killTweensOf(this.panel);
    gsap.to(this.panel, { opacity: 0, scale: 0.9, duration: 0.3, ease: 'power2.in', onComplete });
  }

  /** Any key press closes the help panel. Deliberately does NOT consume the
   *  event — dismiss-and-act is the game's designed behavior (Input.ts itself
   *  calls uiHideHelp() on keydown and keeps processing the key), so pressing
   *  e.g. FIRE while help is open closes help AND fires. A held key's repeats
   *  aren't presses: holding H would open the panel, then shut it as the
   *  repeats began. */
  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.repeat) return;
    this.close();
  };

  /** A click anywhere closes it, on the panel too, as legacy's did and the
   *  hint says. Clicks on the scrollbar don't fire one, so it still scrolls.
   *  Stopped so it doesn't reach the game's window listeners. */
  private handleClick = (e: Event) => {
    e.stopPropagation();
    this.close();
  };

  private close() {
    if (this.isClosing) return;
    this.isClosing = true;
    this.animateOut(() => this.api.closePanel());
  }

  /** Build a bind-name → keybind-item lookup from the reactive columns. */
  private buildBindLookup(): Map<string, KeybindItem> {
    const byName = new Map<string, KeybindItem>();
    for (const item of this.uiState.keybinds.leftColumn) byName.set(item.bindName, item);
    for (const item of this.uiState.keybinds.rightColumn) byName.set(item.bindName, item);
    return byName;
  }

  /** Non-empty key labels (primary, then secondary) for a bind. */
  private keysFor(byName: Map<string, KeybindItem>, bindName: string): string[] {
    const item = byName.get(bindName);
    if (!item) return [];
    return [item.primaryKey, item.secondaryKey].filter((k): k is string => k != null && k !== '');
  }

  override render() {
    if (this.uiState.isMobile) {
      return this.renderMobile();
    }
    return this.renderDesktop();
  }

  private renderMobile() {
    return html`
      <div class="backdrop" @click=${this.handleClick}>
        <div class="panel mobile scrollbar-thin">
          <div class="header">HOW TO PLAY</div>
          <div class="mobile-graphic"></div>
          <div class="section-title">For the best game experience play on a PC</div>
          <div class="close">Tap anywhere to continue</div>
        </div>
      </div>
    `;
  }

  private renderDesktop() {
    const condensed = this.uiState.helpCondensed;
    const byName = this.buildBindLookup();
    const helpKey = byName.get('HELP')?.primaryKey;

    return html`
      <div class="backdrop" @click=${this.handleClick}>
        <div class="panel scrollbar-thin ${condensed ? '' : 'full'}">
          <div class="header">
            HOW TO PLAY${helpKey ? html`<span class="keybind-hint">(${helpKey})</span>` : nothing}
          </div>
          <div class="close">press a button or click anywhere to hide</div>

          <div class="body">
            <div class="essentials">
              ${this.renderHero(byName)}

              <div class="gamepadenabled">Gamepads are also supported</div>
            </div>

            ${condensed ? nothing : this.renderReference(byName)}
          </div>
        </div>
      </div>
    `;
  }

  /** Visual movement / fire / special / upgrade section. */
  private renderHero(byName: Map<string, KeybindItem>) {
    const fireKeys = this.keysFor(byName, 'FIRE');
    const specialKeys = this.keysFor(byName, 'SPECIAL');
    const upgradeKeys = ['UPGRADE1', 'UPGRADE2', 'UPGRADE3', 'UPGRADE4']
      .map((b) => byName.get(b)?.primaryKey)
      .filter((k): k is string => k != null && k !== '');

    return html`
      <!-- Movement: each direction shows its primary key — the iconic arrow
           glyph when bound to an arrow key, otherwise the key label (WASD etc). -->
      <div class="keybind">
        <div class="keys">
          ${MOVEMENT_BINDS.map((bind) => {
            const key = byName.get(bind)?.primaryKey ?? '';
            const glyph = ARROW_KEY_GLYPH[key];
            return glyph
              ? html`<div class="key small">
                  &nbsp;
                  <div class="arrow ${glyph}"></div>
                </div>`
              : html`<div class="key small">${key || '—'}</div>`;
          })}
        </div>
        <div class="keydesc">Movement</div>
      </div>

      <!-- Fire -->
      <div class="keybind">
        <div class="keys">${fireKeys.map((k) => html`<div class="key">${k}</div>`)}</div>
        <div class="keydesc">Fire</div>
      </div>

      <!-- Special ability -->
      <div class="keybind">
        <div class="keys">
          ${specialKeys.map((k, i) =>
            i === 0
              ? html`<div class="key">${k}</div>`
              : html`<span class="keyor">or</span>
                  <div class="key">${k}</div>`,
          )}
        </div>
        <div class="keydesc">Special ability</div>
      </div>

      <!-- Quick upgrade -->
      <div class="keybind">
        <div class="keys">${upgradeKeys.map((k) => html`<div class="key small">${k}</div>`)}</div>
        <div class="keydesc">Quick upgrade</div>
      </div>
    `;
  }

  /** Grouped, reactive shortcut reference + static chat commands. */
  private renderReference(byName: Map<string, KeybindItem>) {
    // The "Other" group surfaces anything bound but neither documented in a
    // category nor shown in the hero section.
    const otherRows: ReturnType<typeof this.renderShortcutRow>[] = [];
    for (const item of byName.values()) {
      if (HERO_BINDS.has(item.bindName) || CATEGORIZED_BINDS.has(item.bindName)) continue;
      const keys = this.keysFor(byName, item.bindName);
      if (keys.length === 0) continue;
      otherRows.push(this.renderShortcutRow(item.displayName, keys, BIND_NOTES[item.bindName]));
    }

    return html`
      <div class="reference">
        <div class="section-title">Shortcuts</div>
        <div class="shortcuts">
          ${SHORTCUT_CATEGORIES.map((cat) => this.renderCategory(byName, cat))}
          ${
            otherRows.length > 0
              ? html`<div class="category">
                  <div class="category-title">Other</div>
                  ${otherRows}
                </div>`
              : nothing
          }
        </div>
      </div>

      <div class="chat-commands">
        <div class="section-title">Chat commands</div>
        <div class="cmdlist">
          ${CHAT_COMMANDS.map(
            ({ cmd, desc }) => html`
              <span class="pair">
                <span class="cmd">${cmd}</span>
                <span class="desc">${desc}</span>
              </span>
            `,
          )}
        </div>
      </div>
    `;
  }

  private renderCategory(byName: Map<string, KeybindItem>, cat: ShortcutCategory) {
    return html`
      <div class="category">
        <div class="category-title">${cat.title}</div>
        ${cat.rows.map((row) => {
          if (typeof row !== 'string') return this.renderShortcutRow(row.label, row.keys, row.note);
          const item = byName.get(row);
          if (!item) return nothing;
          const keys = this.keysFor(byName, row);
          if (keys.length === 0) return nothing;
          return this.renderShortcutRow(item.displayName, keys, BIND_NOTES[row]);
        })}
      </div>
    `;
  }

  private renderShortcutRow(label: string, keys: readonly string[], note?: string) {
    return html`
      <div class="srow">
        <span class="skeys">
          ${keys.map((k, i) =>
            i === 0
              ? html`<span class="skey">${k}</span>`
              : html`<span class="sor">or</span><span class="skey">${k}</span>`,
          )}
        </span>
        <span class="slabel">${label}${note ? html`<span class="snote">${note}</span>` : nothing}</span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'help-panel': HelpPanel;
  }
}
